import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const DOT = { done: "bg-emerald-600 border-emerald-600", current: "bg-white border-amber-500 ring-2 ring-amber-200", todo: "bg-white border-stone-300" };
const inr = (p) => `₹${(p / 100).toLocaleString("en-IN")}`;

// Lead / ambulance status pinned in a thread — server-truth, participant-only (/conversations/:id/journey).
export function JourneyCard({ convId, refreshKey }) {
  const { t } = useI18n();
  const [j, setJ] = useState(null);
  useEffect(() => {
    const load = () => { if (!document.hidden) api.get(`/conversations/${convId}/journey`).then((r) => setJ(r.data)).catch(() => setJ(null)); };
    load();
    const t = setInterval(load, 20000);   // picks up payments/status changes made on other screens
    return () => clearInterval(t);
  }, [convId, refreshKey]);
  if (!j || j.kind === "none" || j.missing) return null;
  const pay = j.payment || {};
  return (
    <div className="mx-3 mt-2 rounded-2xl border border-stone-200 bg-white px-3 py-2.5" data-testid="thread-journey">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-black uppercase tracking-widest text-[10px] text-stone-500">{t(`journey.kind.${j.kind}`, j.kind === "lead" ? "Service request" : j.kind === "order" ? "Order" : "Ambulance trip")}</span>
        <span className="font-bold text-stone-800 truncate" data-testid="thread-journey-title">{j.title}</span>
        {j.booking && <span className="text-stone-500 shrink-0" data-testid="thread-journey-amount">{inr(j.booking.amount_paise)}</span>}
      </div>
      <ol className="mt-2 flex items-center gap-1 flex-wrap" data-testid="thread-journey-steps" aria-label={j.steps.map((s) => `${t(`journey.step.${s.key}`, s.label)}: ${t(`journey.state.${s.state}`, s.state)}`).join(", ")}>
        {j.steps.map((s, i) => (
          <li key={s.key} className="flex items-center gap-1 min-w-0" data-testid={`thread-journey-step-${s.key}`} data-state={s.state}>
            <span className={`w-2.5 h-2.5 rounded-full border-2 shrink-0 ${DOT[s.state]}`} />
            <span className={`text-[11px] whitespace-nowrap ${s.state === "current" ? "inline text-amber-800 font-black" : "hidden sm:inline"} ${s.state === "todo" ? "text-stone-400" : s.state === "done" ? "text-stone-800 font-bold" : ""}`}>{t(`journey.step.${s.key}`, s.label)}</span>
            {i < j.steps.length - 1 && <span className="w-3 h-px bg-stone-300 shrink-0" />}
          </li>
        ))}
      </ol>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {pay.status && pay.status !== "none" && (
          <span className="text-[11px] text-stone-600" data-testid="thread-journey-payment">{t("journey.payment", "Payment")}: <b>{t(`orders.pay.${pay.status}`, pay.status)}</b></span>
        )}
        {pay.test_mode && <span className="rounded-full bg-amber-200 text-amber-900 px-2 text-[10px] font-black" data-testid="thread-journey-test-mode">{t("pay.test_mode", "TEST MODE · no real money")}</span>}
        {(j.lead_status === "withdrawn" || j.lead_status === "closed" || j.terminal_off_path) && (
          <span className="text-[11px] font-bold text-stone-600" data-testid="thread-journey-closed">{t(`journey.closed.${j.trip_status || j.order_status || j.lead_status}`, j.closed_label || (j.lead_status === "withdrawn" ? "Withdrawn" : "Closed"))} · {t("journey.closed_suffix", "this request is closed")}</span>
        )}
        {j.next_action && (
          <Link to={j.next_action.href} className="ms-auto h-8 px-3 rounded-full bg-stone-900 text-white text-xs font-bold inline-flex items-center"
                data-testid="thread-journey-next">{j.next_action.code ? t(`journey.next.${j.next_action.code}`, j.next_action.label, { n: j.next_action.n }) : j.next_action.label}</Link>
        )}
      </div>
    </div>
  );
}
